import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CategoryCard from './CategoryCard';

const DESKTOP_COLS = 5;
const DESKTOP_ROWS = 2;
const MOBILE_COLS = 3;
const MOBILE_ROWS = 3;

function chunkArray(items, size) {
  if (size <= 0) return [];
  const pages = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages.length ? pages : [[]];
}

export default function CategoryCarousel({ categories, onCategoryClick, noImageLabel }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const cols = isDesktop ? DESKTOP_COLS : MOBILE_COLS;
  const rows = isDesktop ? DESKTOP_ROWS : MOBILE_ROWS;
  const pageSize = cols * rows;

  const pages = useMemo(() => chunkArray(categories, pageSize), [categories, pageSize]);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  useEffect(() => {
    setPageIndex((prev) => Math.min(prev, Math.max(0, pages.length - 1)));
  }, [pages.length]);

  const canGoPrev = pageIndex > 0;
  const canGoNext = pageIndex < pages.length - 1;
  const showNav = pages.length > 1;
  const isRtl = theme.direction === 'rtl';
  const PrevIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon;
  const NextIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon;

  const navButtonSx = {
    width: { xs: 44, md: 48 },
    height: { xs: 44, md: 48 },
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    color: 'primary.main',
    boxShadow: '0 3px 12px rgba(42,147,143,0.14)',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      borderColor: 'primary.light',
    },
    '&.Mui-disabled': {
      opacity: 0.4,
      color: 'text.disabled',
    },
  };

  return (
    <Box>
      <Box
        key={`${pageSize}-${pageIndex}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gridAutoRows: '1fr',
          alignItems: 'stretch',
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          animation: 'categoryFadeIn 0.28s ease',
          '@keyframes categoryFadeIn': {
            from: { opacity: 0, transform: 'translateY(6px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {pages[pageIndex].map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            onClick={() => onCategoryClick(category)}
            noImageLabel={noImageLabel}
          />
        ))}
      </Box>

      {showNav && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mt: { xs: 2.5, md: 3 },
            pt: { xs: 2, md: 2.5 },
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={{ xs: 2.5, sm: 3, md: 3.5 }}
            sx={{ mx: 'auto' }}
          >
            <IconButton
              aria-label="previous categories"
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={!canGoPrev}
              sx={navButtonSx}
            >
              <PrevIcon />
            </IconButton>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={{ xs: 1.5, md: 2 }}
              sx={{ minWidth: { xs: 88, md: 110 } }}
            >
              <Typography
                component="span"
                fontWeight={800}
                sx={{
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  color: 'primary.main',
                  lineHeight: 1,
                  letterSpacing: 0.4,
                }}
              >
                {pageIndex + 1}
              </Typography>
              <Typography
                component="span"
                fontWeight={600}
                sx={{
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  color: 'text.secondary',
                  lineHeight: 1,
                }}
              >
                /
              </Typography>
              <Typography
                component="span"
                fontWeight={700}
                sx={{
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  color: 'text.secondary',
                  lineHeight: 1,
                }}
              >
                {pages.length}
              </Typography>
            </Stack>

            <IconButton
              aria-label="next categories"
              onClick={() => setPageIndex((p) => Math.min(pages.length - 1, p + 1))}
              disabled={!canGoNext}
              sx={navButtonSx}
            >
              <NextIcon />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
